import React, { useState, useRef, useEffect } from 'react';
import Moment from 'moment';
import 'moment/locale/es';

Moment.locale('es');

// Selector de rango de fechas en una sola vista tipo calendario (estilo
// LATAM): un campo que muestra "Desde – Hasta" y al hacer click abre un
// panel con DOS meses uno al lado del otro donde se elige el inicio y el
// fin del rango con clicks sobre los días, sin inputs de texto/fecha
// separados. Reemplaza el patrón anterior de dos <input type="date">.
const WEEKDAY_LABELS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
const MONTH_LABELS = Moment.months();
const YEAR_RANGE_START = 2020;
const YEARS = (() => {
    const currentYear = Moment().year();
    const years = [];
    for (let y = currentYear; y >= YEAR_RANGE_START; y--) years.push(y);
    return years;
})();

const QUICK_RANGES = [
    { label: 'Hoy', get: () => [Moment(), Moment()] },
    { label: 'Ayer', get: () => [Moment().subtract(1, 'day'), Moment().subtract(1, 'day')] },
    { label: 'Últimos 7 días', get: () => [Moment().subtract(6, 'days'), Moment()] },
    { label: 'Últimos 30 días', get: () => [Moment().subtract(29, 'days'), Moment()] },
    { label: 'Este mes', get: () => [Moment().startOf('month'), Moment()] },
];

function toKey(m) {
    return m.format('YYYY-MM-DD');
}

// grilla de semanas completas (lunes a domingo) que cubren el mes dado
function buildMonthGrid(monthStart) {
    const start = monthStart.clone().startOf('month').startOf('isoWeek');
    const end = monthStart.clone().endOf('month').endOf('isoWeek');
    const days = [];
    let cur = start.clone();
    while (cur.isSameOrBefore(end, 'day')) {
        days.push(cur.clone());
        cur.add(1, 'day');
    }
    return days;
}

function useIsNarrow(breakpoint) {
    const [isNarrow, setIsNarrow] = useState(
        typeof window !== 'undefined' && window.innerWidth < breakpoint
    );
    useEffect(() => {
        function onResize() {
            setIsNarrow(window.innerWidth < breakpoint);
        }
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [breakpoint]);
    return isNarrow;
}

function SelectChip({ children, ariaLabel, title, onChange, value, style }) {
    return (
        <div className="drp-chip" style={{
            position: 'relative', display: 'flex', alignItems: 'center',
            border: '1px solid #e2e2e2', borderRadius: 5, background: '#fafafa',
            padding: '2px 18px 2px 8px',
        }}>
            <select
                aria-label={ariaLabel}
                title={title}
                value={value}
                onChange={onChange}
                style={{
                    border: 'none', background: 'transparent', color: '#333',
                    fontWeight: 600, fontSize: 13, cursor: 'pointer', textTransform: 'capitalize',
                    WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                    ...style,
                }}
            >
                {children}
            </select>
            <i className="fa fa-caret-down" style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                fontSize: 11, color: '#999', pointerEvents: 'none',
            }} />
        </div>
    );
}

function MonthYearSelect({ monthStart, onYearChange, onMonthChange, minKey, maxKey }) {
    function handleMonthChange(e) {
        onMonthChange(monthStart.clone().month(Number(e.target.value)).startOf('month'));
    }
    function handleYearChange(e) {
        onYearChange(monthStart.clone().year(Number(e.target.value)).startOf('month'));
    }
    function monthDisabled(i) {
        const candidate = monthStart.clone().month(i);
        if (minKey && toKey(candidate.clone().endOf('month')) < minKey) return true;
        if (maxKey && toKey(candidate.clone().startOf('month')) > maxKey) return true;
        return false;
    }
    function yearDisabled(y) {
        if (minKey && y < Moment(minKey).year()) return true;
        if (maxKey && y > Moment(maxKey).year()) return true;
        return false;
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <SelectChip
                ariaLabel="Mes"
                title="Cambiar mes"
                value={monthStart.month()}
                onChange={handleMonthChange}
            >
                {MONTH_LABELS.map((m, i) => (
                    <option key={m} value={i} disabled={monthDisabled(i)} style={{ textTransform: 'capitalize' }}>{m}</option>
                ))}
            </SelectChip>
            <SelectChip
                ariaLabel="Año"
                title="Cambiar año"
                value={monthStart.year()}
                onChange={handleYearChange}
            >
                {(YEARS.includes(monthStart.year()) ? YEARS : [monthStart.year(), ...YEARS]).map((y) => (
                    <option key={y} value={y} disabled={yearDisabled(y)}>{y}</option>
                ))}
            </SelectChip>
        </div>
    );
}

function MonthGrid({ monthStart, from, to, hoverKey, onDayClick, onDayHover, width, onPrev, onNext, onYearChange, onMonthChange, minKey, maxKey }) {
    const days = buildMonthGrid(monthStart);
    const hasHeaderNav = !!onPrev;

    return (
        <div style={{ width }}>
            {hasHeaderNav ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <button
                        type="button"
                        className="drp-nav-btn"
                        onClick={onPrev}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px 6px', borderRadius: 4 }}
                        aria-label="Mes anterior"
                    >
                        <i className="fa fa-chevron-left" style={{ color: '#666' }} />
                    </button>
                    <MonthYearSelect monthStart={monthStart} onYearChange={onYearChange} onMonthChange={onMonthChange} minKey={minKey} maxKey={maxKey} />
                    <button
                        type="button"
                        className="drp-nav-btn"
                        onClick={onNext}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px 6px', borderRadius: 4 }}
                        aria-label="Mes siguiente"
                    >
                        <i className="fa fa-chevron-right" style={{ color: '#666' }} />
                    </button>
                </div>
            ) : (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                    <MonthYearSelect monthStart={monthStart} onYearChange={onYearChange} onMonthChange={onMonthChange} minKey={minKey} maxKey={maxKey} />
                </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                {WEEKDAY_LABELS.map((wd) => (
                    <div key={wd} style={{ textAlign: 'center', fontSize: 11, color: '#aaa', fontWeight: 600, padding: '2px 0' }}>
                        {wd}
                    </div>
                ))}
                {days.map((day) => {
                    const key = toKey(day);
                    const inMonth = day.isSame(monthStart, 'month');
                    const isStart = from && key === from;
                    const isEnd = to && key === to;
                    const rangeEnd = to || hoverKey;
                    const inRange = from && rangeEnd && key > from && key < rangeEnd;
                    const isToday = key === toKey(Moment());
                    const outOfBounds = (minKey && key < minKey) || (maxKey && key > maxKey);

                    let background = 'transparent';
                    let color = inMonth ? '#333' : '#ccc';
                    let borderRadius = 6;
                    if (inRange) {
                        background = '#e8f6ee';
                        borderRadius = 0;
                    }
                    if (isStart || isEnd) {
                        background = '#27ae60';
                        color = '#fff';
                    }
                    if (outOfBounds && !isStart && !isEnd) {
                        color = '#e3e3e3';
                    }

                    return (
                        <button
                            key={key}
                            type="button"
                            className="drp-day"
                            disabled={outOfBounds}
                            onClick={() => onDayClick(key)}
                            onMouseEnter={() => onDayHover(key)}
                            style={{
                                border: isToday && !isStart && !isEnd ? '1px solid #27ae60' : '1px solid transparent',
                                background,
                                color,
                                borderRadius: isStart ? '6px 0 0 6px' : isEnd ? '0 6px 6px 0' : borderRadius,
                                padding: '6px 0',
                                fontSize: 12,
                                cursor: outOfBounds ? 'not-allowed' : 'pointer',
                                fontWeight: isStart || isEnd ? 700 : 400,
                            }}
                        >
                            {day.date()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export function DateRangeInput({ from, to, onApply, disabled, disabledHint }) {
    const [open, setOpen] = useState(false);
    const [draftFrom, setDraftFrom] = useState(from);
    const [draftTo, setDraftTo] = useState(to);
    const [hoverKey, setHoverKey] = useState(null);
    const [leftMonth, setLeftMonth] = useState(Moment(from || undefined).startOf('month'));
    const [rightMonth, setRightMonth] = useState(Moment(from || undefined).startOf('month').add(1, 'month'));
    const wrapRef = useRef(null);
    const isNarrow = useIsNarrow(600);

    useEffect(() => {
        setDraftFrom(from);
        setDraftTo(to);
    }, [from, to]);

    useEffect(() => {
        function onClickOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    function label() {
        if (!from) return 'Seleccionar fechas';
        const f = Moment(from).format('DD MMM YYYY');
        if (!to || to === from) return f;
        return f + ' – ' + Moment(to).format('DD MMM YYYY');
    }

    function toggle() {
        if (disabled) return;
        const f = Moment(from || undefined).startOf('month');
        setDraftFrom(from);
        setDraftTo(to);
        setHoverKey(null);
        setLeftMonth(f);
        setRightMonth(f.clone().add(1, 'month'));
        setOpen((o) => !o);
    }

    function onDayClick(key) {
        // primer click de una nueva selección (o se reinicia porque ya había un rango completo)
        if (!draftFrom || (draftFrom && draftTo)) {
            setDraftFrom(key);
            setDraftTo(null);
            return;
        }
        // click antes del inicio -> reinicia el inicio ahí
        if (key < draftFrom) {
            setDraftFrom(key);
            setDraftTo(null);
            return;
        }
        setDraftTo(key);
    }

    function setRangeStart(key) {
        setDraftFrom(key);
        setDraftTo((prevTo) => (prevTo && prevTo >= key) ? prevTo : null);
    }

    function setRangeEnd(key) {
        setDraftTo(key);
        setDraftFrom((prevFrom) => (prevFrom && prevFrom <= key) ? prevFrom : key);
    }

    function selectStart(monthMoment) {
        setRangeStart(toKey(monthMoment.clone().startOf('month')));
    }

    function selectEnd(monthMoment) {
        setRangeEnd(toKey(monthMoment.clone().endOf('month')));
    }

    function applyQuickRange(range) {
        const [f, t] = range.get();
        const fKey = toKey(f);
        const tKey = toKey(t);
        onApply(fKey, tKey);
        setOpen(false);
    }

    function apply() {
        if (!draftFrom) return;
        onApply(draftFrom, draftTo || draftFrom);
        setOpen(false);
    }

    function rangeLabel() {
        if (!draftFrom) return 'Elegí la fecha de inicio';
        if (!draftTo) return Moment(draftFrom).format('DD MMM YYYY') + ' — elegí la fecha final';
        return Moment(draftFrom).format('DD MMM YYYY') + ' – ' + Moment(draftTo).format('DD MMM YYYY');
    }

    return (
        <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
            <style>{`
              .drp-day:hover { filter: brightness(0.95); }
              .drp-day:disabled { cursor: not-allowed; }
              .drp-day:disabled:hover { filter: none; }
              .drp-nav-btn:hover { background: #f0f0f0; }
              .drp-quick-btn:hover { background: #e8f6ee; border-color: #27ae60; color: #1e8449; }
              .drp-chip:hover { background: #e8f6ee; border-color: #27ae60; }
            `}</style>

            <button
                type="button"
                className="form-control"
                onClick={toggle}
                disabled={disabled}
                title={disabled ? disabledHint : undefined}
                style={{
                    display: 'flex', alignItems: 'center', gap: 8, minWidth: 210, maxWidth: '100%',
                    textAlign: 'left', background: disabled ? '#f4f4f4' : '#fff',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    color: disabled ? '#aaa' : undefined,
                }}
            >
                <i className="fa fa-calendar" style={{ color: disabled ? '#bbb' : '#27ae60' }} />
                {label()}
            </button>

            {open && !disabled && (
                <div
                    style={{
                        position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 20,
                        background: '#fff', border: '1px solid #ddd', borderRadius: 8,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.14)', padding: isNarrow ? 12 : 14,
                        width: isNarrow ? 250 : 'auto',
                        maxWidth: 'calc(100vw - 32px)',
                    }}
                >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                        {QUICK_RANGES.map((r) => (
                            <button
                                key={r.label}
                                type="button"
                                className="drp-quick-btn"
                                onClick={() => applyQuickRange(r)}
                                style={{
                                    border: '1px solid #ddd', borderRadius: 14, padding: '4px 10px',
                                    fontSize: 11.5, color: '#555', background: '#fafafa', cursor: 'pointer',
                                }}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>

                    {isNarrow ? (
                        <MonthGrid
                            monthStart={leftMonth}
                            from={draftFrom}
                            to={draftTo}
                            hoverKey={hoverKey}
                            onDayClick={onDayClick}
                            onDayHover={setHoverKey}
                            width="100%"
                            onPrev={() => setLeftMonth(leftMonth.clone().subtract(1, 'month'))}
                            onNext={() => setLeftMonth(leftMonth.clone().add(1, 'month'))}
                            onYearChange={(m) => { setLeftMonth(m); onDayClick(toKey(m)); }}
                            onMonthChange={(m) => { setLeftMonth(m); onDayClick(toKey(m.clone().endOf('month'))); }}
                        />
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                            <MonthGrid
                                monthStart={leftMonth}
                                from={draftFrom}
                                to={draftTo}
                                hoverKey={hoverKey}
                                onDayClick={setRangeStart}
                                onDayHover={setHoverKey}
                                width={230}
                                maxKey={draftTo || null}
                                onPrev={() => setLeftMonth(leftMonth.clone().subtract(1, 'month'))}
                                onNext={() => setLeftMonth(leftMonth.clone().add(1, 'month'))}
                                onYearChange={(m) => { setLeftMonth(m); selectStart(m); }}
                                onMonthChange={(m) => { setLeftMonth(m); selectStart(m); }}
                            />

                            <MonthGrid
                                monthStart={rightMonth}
                                from={draftFrom}
                                to={draftTo}
                                hoverKey={hoverKey}
                                onDayClick={setRangeEnd}
                                onDayHover={setHoverKey}
                                width={230}
                                minKey={draftFrom || null}
                                onPrev={() => setRightMonth(rightMonth.clone().subtract(1, 'month'))}
                                onNext={() => setRightMonth(rightMonth.clone().add(1, 'month'))}
                                onYearChange={(m) => { setRightMonth(m); selectEnd(m); }}
                                onMonthChange={(m) => { setRightMonth(m); selectEnd(m); }}
                            />
                        </div>
                    )}

                    <div style={{
                        display: 'flex', flexDirection: isNarrow ? 'column' : 'row',
                        alignItems: isNarrow ? 'stretch' : 'center', justifyContent: 'space-between',
                        marginTop: 12, paddingTop: 10, borderTop: '1px solid #f0f0f0', gap: 10,
                    }}>
                        <span style={{ fontSize: 12, color: '#666' }}>{rangeLabel()}</span>
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={() => setOpen(false)}
                                style={isNarrow ? { flex: 1 } : undefined}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={!draftFrom}
                                onClick={apply}
                                style={isNarrow ? { flex: 1 } : undefined}
                            >
                                Aplicar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DateRangeInput;
